import { useState } from "react";

import { ExportOrganisationsButton } from "components/organisations/ExportOrganisationsButton";
import Divider from "components/ui/divider/Divider";
import Modal from "components/ui/modal/Modal";
import { PaywallProvider } from "lib/providers/Paywall";
import { ProHelperProvider } from "lib/providers/ProHelper";
import { ExportOrgCountField, SearchOrgRole } from "lib/types/graphQLEnums";
import { useTablePageSize } from "../../lib/hooks/useTablePageSize";
import { SaveSupplierButton } from "./SaveSupplierButton";
import { SupplierTable } from "./SupplierTable";
import { DEFAULT_FILTERS, SupplierColumns } from "./utils";

import css from "../organisations/OrganisationsModal.module.scss";

const SUPPLIER_MODAL_COLUMNS: SupplierColumns[] = ["name", "notices", "sme"];

export type SuppliersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guids: string[];
};

export function SuppliersModal({ open, onOpenChange, guids }: SuppliersModalProps) {
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>(guids);

  const supplierGuids = selectedSuppliers.length > 0 ? selectedSuppliers : guids;

  const [pageSize, setPageSize] = useTablePageSize("suppliers-modal-table");
  const [currentPage, setCurrentPage] = useState(1);

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <PaywallProvider>
        <ProHelperProvider>
          <div className={css.modalContainer}>
            <h3 className={css.title}>Suppliers awarded this contract</h3>
            <Divider className={css.divider} />
            <div className={css.modal}>
              <div className={css.modalHeader}>
                <h3>{supplierGuids.length} suppliers</h3>

                <div className={css.actions}>
                  {selectedSuppliers.length > 0 && (
                    <SaveSupplierButton
                      supplierGuids={supplierGuids}
                      trackingData={{ "Context source": "Notice preview" }}
                    />
                  )}
                  <ExportOrganisationsButton
                    filters={{
                      ...DEFAULT_FILTERS,
                      ids: supplierGuids,
                      role: SearchOrgRole.Supplier,
                      isSme:
                        typeof DEFAULT_FILTERS.isSme === "boolean"
                          ? DEFAULT_FILTERS.isSme
                          : undefined,
                      listIds: DEFAULT_FILTERS.buyerLists,
                      anchorOrganisation: undefined,
                      limit: pageSize,
                      page: currentPage,
                    }}
                    selectedOrganisationIds={supplierGuids}
                    countFields={[ExportOrgCountField.AwardCount]}
                  />
                </div>
              </div>
              <div className={css.table}>
                <SupplierTable
                  filters={{
                    ...DEFAULT_FILTERS,
                    guids,
                  }}
                  pageSize={pageSize}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  setPageSize={setPageSize}
                  selectedColumns={SUPPLIER_MODAL_COLUMNS}
                  selectedRows={selectedSuppliers}
                  onSelectedRowsChange={(rows) => setSelectedSuppliers(rows)}
                />
              </div>
            </div>
          </div>
        </ProHelperProvider>
      </PaywallProvider>
    </Modal>
  );
}
